import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api, { getAllSites, getOpenDefects } from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import { isAdmin } from '../../../lib/roles';
import { formatDisplayShort } from '../../../lib/dateUtils';
import { ELEMENT_TYPE_OPTIONS, sortSessionsByElementTypeOrder } from '../../../constants/AssetTestConfig';
import { isOwnInProgressSession } from '../../../lib/sessionOwnership';

/** Group assets by element_type: electrical (default) vs fitments (Workshop Equipment). */
function countByElementType(assets) {
  let electrical = 0;
  let fitments = 0;
  assets.forEach((a) => {
    const et = (a.element_type || 'electrical').trim().toLowerCase();
    if (et === 'fitments') fitments += 1;
    else electrical += 1;
  });
  return { electrical, fitments };
}

const ELEMENT_TYPE_LABEL_MAP = Object.fromEntries(
  ELEMENT_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

/**
 * Page-level data hook for SiteDetails.jsx. Owns all queries, derived memos,
 * and navigation handlers. The page component calls this once and receives
 * everything it needs to render.
 */
export function useSiteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? user?.userId;
  const canDeleteSessions = isAdmin(user);
  const [activeTab, setActiveTab] = useState('overview');

  const { data: sites, isLoading: sitesLoading, error: sitesError } = useQuery(
    'sites',
    () => getAllSites(),
    { staleTime: 60 * 1000 }
  );

  const { data: repairsPayload, isLoading: repairsLoading, error: repairsError } = useQuery(
    ['siteOpenDefects', id],
    () => getOpenDefects({ siteId: id, page: 1, limit: 100 }),
    { staleTime: 30000, enabled: !!id }
  );

  const { data: assetsPayload, error: assetsError } = useQuery(
    ['sessionAssets', id, 'summary-full'],
    async () => {
      const all = [];
      let page = 1;
      let hasNext = true;
      while (hasNext) {
        const res = await api.get(`/assets/site/${id}`, { params: { page, limit: 100 } });
        const data = res.data;
        all.push(...(data.assets || []));
        hasNext = data.pagination?.hasNextPage === true;
        page += 1;
      }
      return { assets: all, pagination: { totalCount: all.length } };
    },
    { staleTime: 60 * 1000, enabled: !!id }
  );

  const { data: siteSessions = [], isLoading: sessionsLoading, error: sessionsError } = useQuery(
    ['sessions-site', id],
    () => api.get(`/sessions/site/${id}`).then((res) => res.data),
    { staleTime: 60 * 1000, enabled: !!id }
  );

  const deleteSessionMutation = useMutation(
    (sessionId) => api.delete(`/sessions/${sessionId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessions-site', id]);
        toast.success('Session deleted.');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to delete session.');
      },
    }
  );

  const { data: inProgressRaw, isError: inProgressError, error: inProgressErrorDetail } = useQuery(
    ['sessions-in-progress', userId],
    () => api.get('/sessions/in-progress').then((res) => res.data),
    { staleTime: 0, enabled: !!userId }
  );

  // Org-wide, not just yours: a colleague's open session at this site is listed too, so the header
  // can show who has the job. Only the owner gets a Resume control (see SiteSessionAction).
  const inProgressSessions = inProgressError ? [] : (inProgressRaw ?? []);

  const inProgressSessionsThisSite = useMemo(() => {
    const rows = inProgressSessions.filter((s) => String(s.site_id) === String(id));
    // Own sessions first, matching the Sites list: resume buttons above "Started by X" labels.
    return sortSessionsByElementTypeOrder(rows).sort(
      (a, b) => Number(isOwnInProgressSession(b, user)) - Number(isOwnInProgressSession(a, user))
    );
  }, [inProgressSessions, id, user]);

  const site = (sites || []).find((s) => String(s.id) === String(id));
  const siteName = site?.display_name || site?.name || site?.site_code || 'Site';
  const repairCount = repairsPayload?.pagination?.totalCount ?? 0;
  const repairs = repairsPayload?.defects ?? [];

  const assetsOverview = useMemo(() => {
    const items = assetsPayload?.assets ?? [];
    const { electrical, fitments } = countByElementType(items);
    return {
      total: items.length,
      electrical,
      fitments,
    };
  }, [assetsPayload]);

  const elementTypeLabels = ELEMENT_TYPE_LABEL_MAP;

  const handleStartNewTest = () => {
    navigate('/tests', { state: { prefillSiteId: String(id) } });
  };

  const handleRepairSession = () => {
    navigate('/repairs', { state: { prefillSiteId: String(id) } });
  };

  const addressLine = [site?.address, site?.city, site?.state, site?.postcode].filter(Boolean).join(', ') || '—';
  const addressQuery = [site?.address, site?.city].filter(Boolean).join(', ');
  const addressMapsUrl = addressQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressQuery)}`
    : null;
  const nextDue = site?.testing_due_date ? formatDisplayShort(site.testing_due_date) : '—';
  const statusLabel = repairCount > 0 ? 'Outstanding' : 'Compliant';
  const statusClass = repairCount > 0 ? 'badge badge-warning' : 'badge badge-success';
  const siteDataWarning = repairsError || assetsError || sessionsError || inProgressErrorDetail;

  return {
    id,
    activeTab,
    setActiveTab,
    sitesLoading,
    sitesError,
    site,
    siteName,
    repairCount,
    repairs,
    repairsLoading,
    siteSessions,
    sessionsLoading,
    canDeleteSessions,
    deleteSession: (sessionId) => deleteSessionMutation.mutate(sessionId),
    isDeletingSession: deleteSessionMutation.isLoading,
    inProgressSessionsThisSite,
    assetsOverview,
    elementTypeLabels,
    handleStartNewTest,
    handleRepairSession,
    addressLine,
    addressMapsUrl,
    nextDue,
    statusLabel,
    statusClass,
    siteDataWarning,
  };
}
